import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2, Gift, Mail, Minus, Plus, Calendar, Clock } from "lucide-react";
import { format } from "date-fns";

interface ProducerEvent {
  id: string;
  title: string;
  date: string;
  currency: string | null;
  quantity_vip: number | null;
  quantity_standing: number | null;
  quantity_early_bird: number | null;
  ticket_title_vip: string | null;
  ticket_title_general: string | null;
  ticket_title_early_bird: string | null;
}

interface CompTicketFormProps {
  events: ProducerEvent[];
  producerId: string;
  onSuccess?: () => void;
}

const formSchema = z.object({
  eventId: z.string().min(1, "Please select an event"),
  ticketType: z.enum(["vip", "standing", "early_bird"], {
    required_error: "Please select a ticket type",
  }),
  quantity: z.number().min(1, "Quantity must be at least 1").max(10, "Maximum 10 tickets"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
});

type FormData = z.infer<typeof formSchema>;

interface CreatedTicket {
  reference: string;
  firstName: string;
  lastName?: string;
  email: string;
  eventTitle: string;
  ticketType: string;
  quantity: number;
}

// Cute quantity stepper component
function QuantityStepper({ 
  value, 
  onChange, 
  min = 1, 
  max = 10 
}: { 
  value: number; 
  onChange: (val: number) => void; 
  min?: number; 
  max?: number;
}) {
  return (
    <div className="flex items-center justify-center gap-4">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <Minus className="h-5 w-5" />
      </button>
      
      <div className="relative">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30 transition-transform hover:scale-105">
          <span className="text-2xl font-bold text-primary-foreground">{value}</span>
        </div>
      </div>
      
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

export function CompTicketForm({ events, producerId, onSuccess }: CompTicketFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTicket, setCreatedTicket] = useState<CreatedTicket | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ProducerEvent | null>(null);
  const [showPastEvents, setShowPastEvents] = useState(false);

  // Separate events into upcoming and past
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date();
    const upcoming: ProducerEvent[] = [];
    const past: ProducerEvent[] = [];
    
    events.forEach(event => {
      const eventDate = new Date(event.date);
      if (eventDate >= now) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });
    
    // Sort upcoming by date ascending, past by date descending
    upcoming.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    past.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    return { upcomingEvents: upcoming, pastEvents: past };
  }, [events]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      eventId: "",
      ticketType: undefined,
      quantity: 1,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    },
  });

  const handleEventChange = (eventId: string) => {
    const event = events.find((e) => e.id === eventId);
    setSelectedEvent(event || null);
    form.setValue("ticketType", undefined as any);
    form.setValue("quantity", 1);
  };

  const getAvailableTicketTypes = () => {
    if (!selectedEvent) return [];
    
    const types: { value: "vip" | "standing" | "early_bird"; label: string; available: number }[] = [];
    
    // Show all ticket types regardless of availability for comp tickets
    // This allows producers to issue comp tickets even when sold out
    types.push({
      value: "vip",
      label: selectedEvent.ticket_title_vip || "VIP",
      available: selectedEvent.quantity_vip || 0,
    });
    types.push({
      value: "standing",
      label: selectedEvent.ticket_title_general || "General Admission",
      available: selectedEvent.quantity_standing || 0,
    });
    types.push({
      value: "early_bird",
      label: selectedEvent.ticket_title_early_bird || "Early Bird",
      available: selectedEvent.quantity_early_bird || 0,
    });
    
    return types;
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    console.log("[CompTicket] Starting comp ticket creation...");
    console.log("[CompTicket] Form data:", data);
    console.log("[CompTicket] Selected event:", selectedEvent);
    console.log("[CompTicket] Producer ID:", producerId);
    
    try {
      // Get current user session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      console.log("[CompTicket] Session:", sessionData?.session?.user?.id, sessionError);
      
      // Create the order data
      const orderData: any = {
        event_id: data.eventId,
        user_first_name: data.firstName,
        user_last_name: data.lastName || "",
        user_email: data.email,
        phone_number_complete: data.phoneNumber || null,
        total_amount: 0,
        status: "completed",
        marketing_consent: false,
        terms_accepted: true,
        promo_code: "COMP",
      };

      // Set ticket quantities based on type
      if (data.ticketType === "vip") {
        orderData.vip_ticket_quantity = data.quantity;
        orderData.vip_seats = [];
        orderData.standing_tickets = 0;
        orderData.early_bird_tickets = 0;
      } else if (data.ticketType === "standing") {
        orderData.standing_tickets = data.quantity;
        orderData.vip_ticket_quantity = 0;
        orderData.vip_seats = [];
        orderData.early_bird_tickets = 0;
      } else if (data.ticketType === "early_bird") {
        orderData.early_bird_tickets = data.quantity;
        orderData.vip_ticket_quantity = 0;
        orderData.vip_seats = [];
        orderData.standing_tickets = 0;
      }

      console.log("[CompTicket] Order data to insert:", orderData);

      // Insert the order
      const { data: newOrder, error } = await supabase
        .from("orders")
        .insert(orderData)
        .select("reference, event_name")
        .single();

      console.log("[CompTicket] Insert result:", { newOrder, error });

      if (error) {
        console.error("[CompTicket] Error creating comp ticket:", error);
        console.error("[CompTicket] Error details:", JSON.stringify(error, null, 2));
        throw error;
      }

      // Get ticket type label
      const ticketTypes = getAvailableTicketTypes();
      const ticketTypeLabel = ticketTypes.find(t => t.value === data.ticketType)?.label || data.ticketType;

      setCreatedTicket({
        reference: newOrder.reference,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        eventTitle: selectedEvent?.title || newOrder.event_name || "Event",
        ticketType: ticketTypeLabel,
        quantity: data.quantity,
      });

      toast.success("Complimentary ticket created successfully!");
      onSuccess?.();
    } catch (error: any) {
      console.error("Error creating comp ticket:", error);
      toast.error(error.message || "Failed to create complimentary ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadQRCode = () => {
    if (!createdTicket) return;

    const svg = document.getElementById("comp-ticket-qr");
    if (!svg) return;

    // Create canvas from SVG
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = async () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL("image/png");
      
      // Try Web Share API for mobile
      if (navigator.share && navigator.canShare) {
        try {
          const blob = await (await fetch(pngUrl)).blob();
          const file = new File([blob], `ticket-${createdTicket.reference}.png`, { type: "image/png" });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `Ticket QR Code - ${createdTicket.reference}`,
            });
            return;
          }
        } catch (err) {
          console.log("Web Share failed, falling back to download");
        }
      }
      
      // Fallback: download file
      const link = document.createElement("a");
      link.download = `ticket-${createdTicket.reference}.png`;
      link.href = pngUrl;
      link.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const resetForm = () => {
    setCreatedTicket(null);
    setSelectedEvent(null);
    form.reset();
  };

  // Success state - show QR code
  if (createdTicket) {
    return (
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="p-6 text-center space-y-6">
          <div className="flex items-center justify-center gap-2 text-green-500">
            <CheckCircle2 className="h-8 w-8" />
            <h3 className="text-xl font-bold">Ticket Created!</h3>
          </div>

          <div className="space-y-2">
            <p className="text-foreground font-medium">
              {createdTicket.firstName} {createdTicket.lastName}
            </p>
            <p className="text-sm text-muted-foreground">{createdTicket.email}</p>
            <p className="text-sm text-muted-foreground">
              {createdTicket.quantity}x {createdTicket.ticketType}
            </p>
            <p className="text-sm text-muted-foreground">{createdTicket.eventTitle}</p>
          </div>

          <div className="bg-white p-4 rounded-lg inline-block mx-auto">
            <QRCodeSVG
              id="comp-ticket-qr"
              value={createdTicket.reference}
              size={200}
              level="H"
              includeMargin
            />
          </div>

          <p className="text-sm font-mono text-muted-foreground">
            Reference: {createdTicket.reference}
          </p>

          <Button onClick={downloadQRCode} className="w-full gap-2">
            <Download className="h-4 w-4" />
            Download QR Code
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
            <Mail className="h-4 w-4 flex-shrink-0" />
            <span>The official ticket has been emailed to the guest.</span>
          </div>

          <Button variant="outline" onClick={resetForm} className="w-full">
            Create Another Ticket
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          Create Complimentary Ticket
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Event Selection */}
            <FormField
              control={form.control}
              name="eventId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Event</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleEventChange(value);
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an event" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background border border-border">
                      {/* Upcoming Events */}
                      {upcomingEvents.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-2 text-primary">
                            <Calendar className="h-3 w-3" />
                            Upcoming Events
                          </SelectLabel>
                          {upcomingEvents.map((event) => (
                            <SelectItem key={event.id} value={event.id}>
                              <div className="flex flex-col">
                                <span>{event.title}</span>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(event.date), "MMM d, yyyy")}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      
                      {/* Past Events Toggle */}
                      {pastEvents.length > 0 && (
                        <>
                          <div className="px-2 py-2 border-t border-border">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowPastEvents(!showPastEvents);
                              }}
                              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
                            >
                              <Clock className="h-3 w-3" />
                              {showPastEvents ? "Hide" : "Show"} Past Events ({pastEvents.length})
                            </button>
                          </div>
                          
                          {showPastEvents && (
                            <SelectGroup>
                              <SelectLabel className="flex items-center gap-2 text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                Past Events
                              </SelectLabel>
                              {pastEvents.map((event) => (
                                <SelectItem key={event.id} value={event.id} className="opacity-70">
                                  <div className="flex flex-col">
                                    <span>{event.title}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(event.date), "MMM d, yyyy")}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Ticket Type Selection */}
            {selectedEvent && (
              <FormField
                control={form.control}
                name="ticketType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ticket Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select ticket type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableTicketTypes().map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label} ({type.available <= 0 ? "sold out" : `${type.available} available`})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Quantity */}
            {selectedEvent && form.watch("ticketType") && (
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-center block">Quantity</FormLabel>
                    <FormControl>
                      <div className="py-4 bg-muted/30 rounded-lg">
                        <QuantityStepper
                          value={field.value}
                          onChange={field.onChange}
                          min={1}
                          max={10}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Guest Information */}
            <div className="border-t border-border pt-6 space-y-4">
              <h4 className="font-medium text-foreground">Guest Information</h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+61 400 000 000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Ticket...
                </>
              ) : (
                <>
                  <Gift className="h-4 w-4 mr-2" />
                  Create Free Ticket
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
